/**
 * The one i18next instance factory (DEN-99 §4). All three catalogs are bundled
 * — no HTTP backend, no language detector, no lazy loader. `pt-BR` is both
 * `fallbackLng` and the authored primary; a missing `en` / `es` key silently
 * falls back to it in every browser build (always NODE_ENV=production), and is
 * loud only under vitest (the frontend setup makes `logger.error` throw).
 */
import { createInstance, type i18n as I18nType } from 'i18next';
import { initReactI18next } from 'react-i18next';
import ptBR from '../.i18n/pt-BR.json';
import en from '../.i18n/en.json';
import es from '../.i18n/es.json';
import { DEFAULT_LOCALE } from './config';
import { reportError } from '../helpers/logging';

const resources = {
  'pt-BR': ptBR as Record<string, Record<string, unknown>>,
  en: en as Record<string, Record<string, unknown>>,
  es: es as Record<string, Record<string, unknown>>,
};

export function createI18n(locale: string): I18nType {
  const instance = createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    resources,
    ns: Object.keys(resources['pt-BR']),
    defaultNS: false,
    nsSeparator: ':',
    keySeparator: '.',
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    saveMissing: true,
    missingKeyHandler: (_lngs, ns, key) => {
      // `vocab.units` is DB-vocab: an admin-created unit with no catalog entry
      // legitimately has no key and renders its pt-BR `name` via `defaultValue`
      // (DEN-99 §8). Only code keys are a bug worth shouting about.
      if (ns === 'vocab.units') {
        return;
      }
      reportError('i18n-missing-key', { ns, key });
    },
  });
  return instance;
}
